/** @format */

import React, { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  Button,
  Card,
  Drawer,
  Form,
  FormItem,
  Input,
  Message,
  Spinner,
  Tooltip
} from '@king-design/react'
import { CountContext } from './index'
import ImportDialog from './ImportDialog'
import ConfigFormDrawer from './ConfigFormDrawer'
import SceneDesigner from './SceneGraph'
import { GraphX6Ref } from 'src/components/GraphX6/types'
import { sceneToJson, validateBeforeSave } from '../container'
import { saveScene, activateScene } from 'src/api/ApiEventOpsScene'
import { approveTrigger } from 'src/api/ApiEventOpsTrigger'
import { useHistory } from 'react-router'
import TestDrawer from './TestDrawer'
const initVersion = {
  versionA: 0,
  versionB: 0,
  versionC: 1,
  version_remark: ''
}

const EditScene: React.FC<{ className?: string }> = ({ className }) => {
  const history = useHistory()
  const { setEditable, sign, sceneInfo } = useContext(CountContext)
  // 表单
  const formRef = useRef(null)
  const [sceneName, setSceneName] = useState(sceneInfo?.Name || '')
  // 导入
  const [importVisible, setImportVisible] = useState(false)
  // 测试
  const [testVisible, setTestVisible] = useState(false)
  const [testID, setTestID] = useState<number>(null)
  // 配置执行表单
  const [configFormVisible, setConfigFormVisible] = useState(false)
  const [formSchema, setFormSchema] = useState<string>(sceneInfo?.FormSchema || '')
  // 新建场景-暂存-保留暂存ID
  const [saveSign, setSaveSign] = useState<string>(sceneInfo?.Sign || undefined)
  // 发布
  const versionFormRef = useRef(null)
  const [versionVisible, setPublishVisible] = useState(false)
  const [version, setVersion] = useState(initVersion)

  useEffect(() => {
    setFormSchema(sceneInfo?.FormSchema || '')
    setSceneName(sceneInfo?.Name || '')
  }, [sceneInfo])

  // 获取提交时数据
  const getSubmitParams = () => {
    const flowData = sceneToJson(sceneDesignerRef?.current?.graph?.toJSON()?.cells || [])
    if (flowData?.Nodes) {
      const { flag, errorNodeName, errorInputKey } = validateBeforeSave(flowData?.Nodes)
      if (flag) {
        Message.error(`节点【${errorNodeName}】输入参数【${errorInputKey}】配置存在错误，请检查！`)
        return
      }
    }
    const params = {
      Name: sceneName,
      Workflow: flowData,
      FormSchema: formSchema || null
    }
    return params
  }

  // 场景设计器ref
  const sceneDesignerRef = useRef<GraphX6Ref>(null)
  // 暂存
  const handleSubmit = async (type?: 'save' | 'publish') => {
    const params = getSubmitParams()
    const saveResult = await saveScene({
      ...params,
      Sign: saveSign
    })
    if (!saveSign) {
      setSaveSign(saveResult.Sign)
    }
    if (saveResult) {
      if (type === 'save') {
        Message.success('保存成功')
        history.push(`/monitor/eventops/scene/detail/${saveResult.Sign}`)
      } else {
        const approveResult = await approveTrigger({
          Module: 'scene',
          TargetID: saveResult.ID,
          VersionNumber: `${version.versionA}.${version.versionB}.${version.versionC}`,
          VersionRemark: version.version_remark
        })
        if (approveResult) {
          Message.success('发布成功')
          history.push(`/monitor/eventops/scene/detail/${saveResult.Sign}`)
          setPublishVisible(false)
          setEditable(false)
          setVersion(initVersion)
        }
      }
    }
  }

  // 测试（预发布场景）
  const handleTest = async () => {
    const params = getSubmitParams()
    const activateResult = await activateScene(params)
    if (activateResult) {
      setTestID(activateResult.ID)
      setTestVisible(true)
    }
  }

  return (
    <Card type="border">
      <div id="edit-scene" className={className}>
        <div className="header">
          <Form starOnRequired={true} layout="inline" ref={formRef}>
            <FormItem label="场景名称" value={sceneName} rules={{ required: true }}>
              <Input
                width={300}
                value={sceneName}
                onChangeValue={value => {
                  setSceneName(value)
                }}
              />
            </FormItem>
          </Form>
          <div className="ksc-space">
            <Button
              type="link"
              onClick={async () => {
                const valid = await formRef?.current?.validate()
                if (valid) {
                  handleTest()
                }
              }}
            >
              测试
            </Button>
            <TestDrawer
              visible={testVisible}
              name={sceneName}
              testID={testID}
              formSchema={formSchema}
              onClose={() => {
                setTestVisible(false)
                setTestID(null)
              }}
            />
            {/* 导入 */}
            {!sign ? (
              <>
                <Button
                  onClick={() => {
                    setImportVisible(true)
                  }}
                >
                  导入
                </Button>
                <ImportDialog
                  visible={importVisible}
                  onClose={data => {
                    if (data) {
                      sceneDesignerRef.current?.graph?.fromJSON(data.cells)
                      sceneDesignerRef.current?.graph?.centerContent()
                      setSceneName(data.formData.name)
                    }
                    setImportVisible(false)
                  }}
                />
              </>
            ) : null}
            {/* 配置执行表单 */}
            <Button onClick={() => setConfigFormVisible(true)}>配置执行表单</Button>
            <ConfigFormDrawer
              visible={configFormVisible}
              formSchema={formSchema}
              onConfirm={(schema: string) => {
                setFormSchema(schema)
              }}
              onClose={() => {
                setConfigFormVisible(false)
              }}
            />
            <Tooltip
              disabled={!sceneInfo?.Approving}
              content="存在审批中的版本，暂不允许此操作。如确实需要，请先撤回审批。"
            >
              <Button
                disabled={sceneInfo?.Approving}
                type="secondary"
                onClick={async () => {
                  const valid = await formRef?.current?.validate()
                  if (valid) {
                    handleSubmit('save')
                  }
                }}
              >
                暂存
              </Button>
            </Tooltip>
            <Tooltip
              disabled={!sceneInfo?.Approving}
              content="存在审批中的版本，暂不允许此操作。如确实需要，请先撤回审批。"
            >
              <Button
                disabled={sceneInfo?.Approving}
                type="primary"
                onClick={async () => {
                  const valid = await formRef?.current?.validate()
                  if (valid) {
                    setPublishVisible(true)
                  }
                }}
              >
                发布
              </Button>
            </Tooltip>
            {sceneInfo?.Sign ? (
              <Button type="flat" onClick={() => setEditable(false)}>
                退出编辑
              </Button>
            ) : null}
          </div>
        </div>
        <div className="content">
          <SceneDesigner ref={sceneDesignerRef} />
        </div>
      </div>
      {/* 版本信息 */}
      <Drawer
        title="版本信息"
        width={600}
        value={versionVisible}
        container={() => document.getElementById('edit-scene')}
        onChangeValue={show => {
          if (!show) {
            setPublishVisible(false)
            setVersion(initVersion)
          }
        }}
        okText="发布"
        ok={async () => {
          const validate = await versionFormRef?.current?.validate()
          if (validate) {
            handleSubmit('publish')
          }
        }}
      >
        <Form className="version_form" starOnRequired ref={versionFormRef} labelWidth={80}>
          <FormItem
            label="新版本号"
            value={true}
            rules={{
              required: true,
              unique: () => {
                if (
                  version.versionA !== undefined &&
                  version.versionB !== undefined &&
                  version.versionC !== undefined
                ) {
                  if (sceneInfo?.CurrentVersion) {
                    const [versionA, versionB, versionC] = sceneInfo?.CurrentVersion.split('.')
                    if (
                      parseInt(versionA) >= version.versionA &&
                      parseInt(versionB) >= version.versionB &&
                      parseInt(versionC) >= version.versionC
                    ) {
                      return '新的版本号需要大于当前版本号'
                    } else {
                      return true
                    }
                  } else {
                    return true
                  }
                } else {
                  return '请填写完整版本号'
                }
              }
            }}
          >
            <div className="form_item_space">
              <FormItem hideLabel>
                <Spinner
                  width={65}
                  min={0}
                  forceStep
                  value={parseInt(version.versionA + '')}
                  onChangeValue={value => {
                    setVersion({ ...version, versionA: value })
                  }}
                />
              </FormItem>
              <p>.</p>
              <FormItem hideLabel>
                <Spinner
                  width={65}
                  min={0}
                  forceStep
                  value={parseInt(version.versionB + '')}
                  onChangeValue={value => {
                    setVersion({ ...version, versionB: value })
                  }}
                />
              </FormItem>
              <p>.</p>
              <FormItem hideLabel>
                <Spinner
                  width={65}
                  min={0}
                  forceStep
                  value={parseInt(version.versionC + '')}
                  onChangeValue={value => {
                    setVersion({ ...version, versionC: value })
                  }}
                />
              </FormItem>
            </div>
            <p className="form_item_extra">{`当前版本为${sceneInfo?.CurrentVersion || '无'}`}</p>
          </FormItem>
          <FormItem label="版本说明" value={version.version_remark} rules={{ required: true }}>
            <Input
              width={450}
              type="textarea"
              rows={4}
              placeholder="请填写版本说明"
              value={version.version_remark}
              onChangeValue={value => setVersion({ ...version, version_remark: value })}
            />
            <p className="form_item_extra">本次更新的重点内容说明</p>
          </FormItem>
        </Form>
      </Drawer>
    </Card>
  )
}

export default styled(EditScene)`
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 40px;
    margin-bottom: 8px;
    .header_right {
    }
  }
  .content {
    padding: 8p 0;
    border: 1px solid #e5e5e5;
  }
  .version_form {
    .form_item_space {
      display: flex;
      p {
        line-height: 32px;
        margin-bottom: 0;
      }
      .k-form-item {
        margin-bottom: 0;
        .k-spinner {
          margin: 0 8px;
        }
        .k-form-error {
          position: static;
        }
      }
    }
    .form_item_extra {
      color: #999;
      margin: 4px 0;
      display: flex;
      align-items: center;
    }
  }
`
